import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Components/Catalyst/button';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { setToken, setRefreshToken } from '../utils/TokenUtils';

/**
 * Password sign-in for environments AuthKit cannot serve — member realm.
 *
 * **Why this exists rather than a form on `/login`.** WorkOS refuses any
 * redirect URI it was not told about in advance, and a PR environment's
 * host is minted per pull request — so AuthKit cannot complete a sign-in
 * on localhost or in `levittown-<PR#>`. Without this there is no way into
 * those environments at all except a script that writes `localStorage` by
 * hand.
 *
 * `LoginPage` stays what it was made into: a door, not a form. This page
 * is deliberately **not linked from it** — you reach it by typing the
 * URL, which is the same posture the backend takes about the endpoint it
 * calls.
 *
 * **It ships in every bundle, including production, and cannot not.** One
 * image serves all environments, so there is no build-time switch to
 * strip it. What makes it safe is that it is inert without the backend:
 * `GET /member-portal/auth/dev-login` is registered only where
 * `DEV_PASSWORD_LOGIN` is
 * true and `ENV` does not contain `prod`, so in production the request
 * 404s and no form is ever rendered. The check is the *absence of a
 * route*, not a configuration value this app could disagree with.
 */
const DevLoginPage: React.FC = () => {
  const navigate = useNavigate();

  // `null` = still asking. Three states, not two: rendering the form
  // while the answer is unknown would flash a password box in production.
  const [offered, setOffered] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Asked with `fetch` rather than the generated client because a 404
    // here is the expected answer in production, and the client's error
    // handling logs it as a failure. This is a question, not a call.
    // Same base the generated clients use (`ApiClients.ts`), read here
    // rather than imported because that module builds axios instances as
    // a side effect and this is a bare question asked before sign-in.
    const api = window._env_?.REACT_APP_AUTH_SVC ?? '';
    fetch(`${api}/api/v1/member-portal/auth/dev-login`)
      .then((r) => setOffered(r.ok))
      .catch(() => setOffered(false));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      // `fetch`, not the generated client. This route is registered
      // only below `prod`, so it is deliberately absent from the OpenAPI
      // document — publishing it would document an endpoint production
      // does not have, and the generator would emit a method that 404s
      // wherever it matters most.
      const api = window._env_?.REACT_APP_AUTH_SVC ?? '';
      const res = await fetch(
        `${api}/api/v1/member-portal/auth/dev-login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        }
      );
      if (!res.ok) {
        setError('Those credentials were not accepted.');
        return;
      }
      const data = await res.json();
      // `true` — this is a deliberate, typed-URL sign-in for testing, and
      // losing it on every tab close would make the page annoying enough
      // that people go back to pasting into devtools.
      setToken(data.access_token, true);
      // Keep `username` in step with the session. Nothing reads it
      // today — `getUserName` has no consumers in either app — but
      // `browser-login.mjs` writes it, so a stale value left here
      // contradicts the token and sends whoever is debugging down the
      // wrong path.
      localStorage.setItem('username', email);
      if (data.refresh_token) setRefreshToken(data.refresh_token, true);
      navigate('/home', { replace: true });
    } catch (err) {
      setError("Couldn't reach the API. Is the backend running?");
    } finally {
      setBusy(false);
    }
  };

  if (offered === null) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center py-8">
        <p className="text-gray-500 dark:text-gray-400">Checking…</p>
      </div>
    );
  }

  if (!offered) {
    return (
      <div className="flex min-h-full flex-1 flex-col justify-center py-8 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white dark:bg-gray-800 px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Not available in this environment
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Password sign-in is offered only below production. Use the
              normal{' '}
              <a href="/login" className="underline">
                sign-in page
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-8 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
          Sign in for testing
        </h2>
        <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
          This environment cannot reach AuthKit, so fixture accounts sign in
          with a password here.
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white dark:bg-gray-800 px-6 py-12 shadow sm:rounded-lg sm:px-12">
          <ErrorAlert className="mb-4">{error}</ErrorAlert>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label
                htmlFor="dev-email"
                className="block text-sm font-medium text-gray-900 dark:text-white"
              >
                Email
              </label>
              <input
                id="dev-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
              />
            </div>

            <div>
              <label
                htmlFor="dev-password"
                className="block text-sm font-medium text-gray-900 dark:text-white"
              >
                Password
              </label>
              <input
                id="dev-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
              />
            </div>

            <Button type="submit" color="sky" className="w-full" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DevLoginPage;
